import { DemoCommand, DelayConfiguration, DEFAULT_DELAYS } from '../types/demoConsts';

const STORAGE_KEY = 'dreamforce-demo-settings';

// Default command mappings
const DEFAULT_COMMANDS = {
  [DemoCommand.ADD_HEALTHCARE_FUNNELS]: 'create setup',
  [DemoCommand.ADD_TREATMENT_REVIEW_STAGE]: 'add stage',
  [DemoCommand.ADD_METADATA_AND_CONNECT]: 'add metadata',
  [DemoCommand.RESET_DEMO]: 'reset',
};

// Combined demo settings interface
interface DemoSettings {
  commands: typeof DEFAULT_COMMANDS;
  delays: DelayConfiguration;
}

// Default settings
const DEFAULT_SETTINGS: DemoSettings = {
  commands: DEFAULT_COMMANDS,
  delays: DEFAULT_DELAYS,
};

// Load settings from localStorage or use defaults
const loadSettings = (): DemoSettings => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<DemoSettings>;
      // Ensure all required settings exist (in case new settings were added)
      return {
        commands: { ...DEFAULT_COMMANDS, ...parsed.commands },
        delays: { ...DEFAULT_DELAYS, ...parsed.delays },
      };
    }
  } catch (error) {
    console.warn('Failed to load demo settings from localStorage:', error);
  }
  return { ...DEFAULT_SETTINGS };
};

// Save settings to localStorage
const saveSettings = (settings: DemoSettings) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.warn('Failed to save demo settings to localStorage:', error);
  }
};

// Initialize with stored or default values
let currentSettings = loadSettings();

// Subscribers for UI updates
const subscribers = new Set<() => void>();

export const demoSettingsStore = {
  // Get current mapping
  getCommandMapping: () => ({ ...currentSettings.commands }),

  // Update a specific command
  updateCommand: (command: DemoCommand, keyword: string) => {
    currentSettings = {
      ...currentSettings,
      commands: {
        ...currentSettings.commands,
        [command]: keyword,
      },
    };
    // Save to localStorage
    saveSettings(currentSettings);
    // Notify subscribers
    subscribers.forEach(callback => callback());
  },

  // Get current delay configuration
  getDelayConfiguration: () => ({ ...currentSettings.delays }),

  // Update a specific delay
  updateDelay: (command: keyof DelayConfiguration, delaySeconds: number) => {
    currentSettings = {
      ...currentSettings,
      delays: {
        ...currentSettings.delays,
        [command]: delaySeconds,
      },
    };
    // Save to localStorage
    saveSettings(currentSettings);
    // Notify subscribers
    subscribers.forEach(callback => callback());
  },

  // Find which command a message triggers
  getCommandForMessage: (message: string): DemoCommand | null => {
    const lowerMessage = message.toLowerCase();

    for (const [command, keyword] of Object.entries(currentSettings.commands)) {
      if (lowerMessage.includes((keyword as string).toLowerCase())) {
        return command as DemoCommand;
      }
    }

    return null;
  },

  // Subscribe to changes (for React components)
  subscribe: (callback: () => void) => {
    subscribers.add(callback);
    return () => subscribers.delete(callback);
  },
};
